/**
 * Reusable Tooltip Component
 *
 * Provides hover tooltips for explaining metrics and terminology.
 * Supports both inline (icon trigger) and wrapper (children trigger) modes.
 */

import { useState, useRef, useEffect, ReactNode } from 'react';

export interface TooltipProps {
  content: ReactNode;
  children?: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  maxWidth?: number;
  showIcon?: boolean;
  iconSize?: 'sm' | 'md';
  className?: string;
}

export function Tooltip({
  content,
  children,
  position = 'top',
  maxWidth = 280,
  showIcon = false,
  iconSize = 'sm',
  className = '',
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [actualPosition, setActualPosition] = useState(position);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Adjust position if tooltip would go off-screen
  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();

      // Check if tooltip would go off-screen and flip if needed
      if (position === 'top' && tooltipRect.top < 8) {
        setActualPosition('bottom');
      } else if (position === 'bottom' && tooltipRect.bottom > window.innerHeight - 8) {
        setActualPosition('top');
      } else if (position === 'left' && tooltipRect.left < 8) {
        setActualPosition('right');
      } else if (position === 'right' && tooltipRect.right > window.innerWidth - 8) {
        setActualPosition('left');
      } else {
        setActualPosition(position);
      }
    }
  }, [isVisible, position]);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-surface-700 border-x-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-surface-700 border-x-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-surface-700 border-y-transparent border-r-transparent',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-surface-700 border-y-transparent border-l-transparent',
  };

  const iconSizeClasses = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
  };

  return (
    <div
      ref={triggerRef}
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {/* Trigger element */}
      {showIcon ? (
        <button
          type="button"
          className="text-text-tertiary hover:text-text-secondary transition-colors cursor-help"
        >
          <InfoIcon className={iconSizeClasses[iconSize]} />
        </button>
      ) : (
        children
      )}

      {/* Tooltip */}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 ${positionClasses[actualPosition]}`}
          style={{ maxWidth }}
        >
          <div className="bg-surface-700 border border-surface-500/50 rounded-lg px-3 py-2 shadow-xl">
            <div className="text-sm text-gray-200">{content}</div>
          </div>
          {/* Arrow */}
          <div
            className={`absolute w-0 h-0 border-4 ${arrowClasses[actualPosition]}`}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Metric Tooltip - Pre-configured tooltip for explaining metrics
 */
export interface MetricTooltipProps {
  metric: keyof typeof METRIC_DEFINITIONS;
  children?: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  locale?: 'en' | 'es';
}

// Standard metric definitions with calculation methodology
const METRIC_DEFINITIONS = {
  loyalty: {
    en: {
      title: 'Brand Loyalty',
      description: 'Measures how likely consumers are to repurchase the brand.',
      calculation: 'Based on repeat purchase behavior (70%) and stated preference data (30%).',
      range: '0-100%, higher = more loyal',
    },
    es: {
      title: 'Lealtad de Marca',
      description: 'Mide la probabilidad de que los consumidores vuelvan a comprar la marca.',
      calculation: 'Basado en comportamiento de recompra (70%) y datos de preferencia declarada (30%).',
      range: '0-100%, mayor = más leal',
    },
  },
  shareVolume: {
    en: {
      title: 'Share of Volume',
      description: 'Percentage of total category units sold by this brand/product.',
      calculation: 'Brand units sold / Total category units sold × 100',
      range: '0-100%',
    },
    es: {
      title: 'Participación de Volumen',
      description: 'Porcentaje de unidades totales de la categoría vendidas por esta marca/producto.',
      calculation: 'Unidades de la marca / Unidades totales de la categoría × 100',
      range: '0-100%',
    },
  },
  shareRevenue: {
    en: {
      title: 'Share of Revenue',
      description: 'Percentage of total category revenue captured by this brand/product.',
      calculation: 'Brand revenue / Total category revenue × 100',
      range: '0-100%',
    },
    es: {
      title: 'Participación de Valor',
      description: 'Porcentaje de ingresos totales de la categoría capturados por esta marca/producto.',
      calculation: 'Ingresos de la marca / Ingresos totales de la categoría × 100',
      range: '0-100%',
    },
  },
  priceSensitivity: {
    en: {
      title: 'Price Sensitivity',
      description: 'How responsive this segment is to price changes.',
      calculation: 'Derived from price elasticity analysis. Higher score = more sensitive to price changes.',
      range: '0-10 scale',
    },
    es: {
      title: 'Sensibilidad al Precio',
      description: 'Qué tan sensible es este segmento a cambios de precio.',
      calculation: 'Derivado del análisis de elasticidad de precio. Mayor puntaje = más sensible.',
      range: 'Escala 0-10',
    },
  },
  elasticity: {
    en: {
      title: 'Price Elasticity',
      description: 'Measures demand responsiveness to price changes.',
      calculation: '% change in quantity demanded / % change in price',
      range: '<-1 elastic, >-1 inelastic',
    },
    es: {
      title: 'Elasticidad de Precio',
      description: 'Mide la respuesta de la demanda a cambios de precio.',
      calculation: '% cambio en cantidad demandada / % cambio en precio',
      range: '<-1 elástico, >-1 inelástico',
    },
  },
  penetration: {
    en: {
      title: 'Market Penetration',
      description: 'Percentage of target consumers who have purchased the product.',
      calculation: 'Buyers / Total target market × 100',
      range: '0-100%',
    },
    es: {
      title: 'Penetración de Mercado',
      description: 'Porcentaje de consumidores objetivo que han comprado el producto.',
      calculation: 'Compradores / Mercado objetivo total × 100',
      range: '0-100%',
    },
  },
  trialRate: {
    en: {
      title: 'Trial Rate',
      description: 'Percentage of aware consumers who tried the product.',
      calculation: 'First-time buyers / Aware consumers × 100',
      range: '0-100%',
    },
    es: {
      title: 'Tasa de Prueba',
      description: 'Porcentaje de consumidores conscientes que probaron el producto.',
      calculation: 'Compradores nuevos / Consumidores conscientes × 100',
      range: '0-100%',
    },
  },
  repeatRate: {
    en: {
      title: 'Repeat Rate',
      description: 'Percentage of trial buyers who repurchased.',
      calculation: 'Repeat buyers / Trial buyers × 100',
      range: '0-100%',
    },
    es: {
      title: 'Tasa de Recompra',
      description: 'Porcentaje de compradores de prueba que recompraron.',
      calculation: 'Compradores repetidores / Compradores de prueba × 100',
      range: '0-100%',
    },
  },
  growthRate: {
    en: {
      title: 'Growth Rate (Volume YoY)',
      description: 'Year-over-year change in unit volume sold.',
      calculation: '(Current year units - Prior year units) / Prior year units × 100',
      range: 'Can be negative (decline) or positive (growth)',
    },
    es: {
      title: 'Tasa de Crecimiento (Volumen YoY)',
      description: 'Cambio año contra año en volumen de unidades vendidas.',
      calculation: '(Unidades año actual - Unidades año anterior) / Unidades año anterior × 100',
      range: 'Puede ser negativo (declive) o positivo (crecimiento)',
    },
  },
  channelShare: {
    en: {
      title: 'Channel Share (Volume)',
      description: 'Percentage of total category volume sold through this channel.',
      calculation: 'Units sold via channel / Total category units × 100',
      range: '0-100%',
    },
    es: {
      title: 'Participación del Canal (Volumen)',
      description: 'Porcentaje del volumen total de la categoría vendido a través de este canal.',
      calculation: 'Unidades vendidas por canal / Unidades totales × 100',
      range: '0-100%',
    },
  },
  channelGrowth: {
    en: {
      title: 'Channel Growth (Volume YoY)',
      description: 'Year-over-year volume growth for this channel.',
      calculation: '(Current year channel units - Prior year) / Prior year × 100',
      range: 'Can be negative or positive',
    },
    es: {
      title: 'Crecimiento del Canal (Volumen YoY)',
      description: 'Crecimiento año contra año de volumen para este canal.',
      calculation: '(Unidades del canal año actual - año anterior) / Año anterior × 100',
      range: 'Puede ser negativo o positivo',
    },
  },
  segmentShare: {
    en: {
      title: 'Segment Share (Volume)',
      description: 'Percentage of total category volume attributed to this consumer segment.',
      calculation: 'Units purchased by segment / Total category units × 100',
      range: '0-100%',
    },
    es: {
      title: 'Participación del Segmento (Volumen)',
      description: 'Porcentaje del volumen total atribuido a este segmento de consumidores.',
      calculation: 'Unidades compradas por segmento / Unidades totales × 100',
      range: '0-100%',
    },
  },
  brandShare: {
    en: {
      title: 'Brand Share (Volume)',
      description: 'Percentage of total category volume sold by this brand.',
      calculation: 'Brand units sold / Total category units × 100',
      range: '0-100%',
    },
    es: {
      title: 'Participación de Marca (Volumen)',
      description: 'Porcentaje del volumen total de la categoría vendido por esta marca.',
      calculation: 'Unidades de la marca / Unidades totales × 100',
      range: '0-100%',
    },
  },
  preferenceShare: {
    en: {
      title: 'Preference Share',
      description: 'Percentage of consumers who prefer this attribute level (from conjoint analysis).',
      calculation: 'Derived from choice-based conjoint survey responses.',
      range: '0-100%, sums to 100% within each attribute',
    },
    es: {
      title: 'Participación de Preferencia',
      description: 'Porcentaje de consumidores que prefieren este nivel de atributo (del análisis conjoint).',
      calculation: 'Derivado de respuestas de encuestas conjoint basadas en elección.',
      range: '0-100%, suma 100% dentro de cada atributo',
    },
  },
};

export function MetricTooltip({
  metric,
  children,
  position = 'top',
  locale = 'en',
}: MetricTooltipProps) {
  const def = METRIC_DEFINITIONS[metric]?.[locale];

  if (!def) return <>{children}</>;

  const content = (
    <div className="space-y-2">
      <div className="font-semibold text-white">{def.title}</div>
      <p className="text-text-secondary">{def.description}</p>
      <div className="pt-2 border-t border-surface-500/50 space-y-1">
        <div className="text-xs">
          <span className="text-text-tertiary">Calculation: </span>
          <span className="text-text-secondary">{def.calculation}</span>
        </div>
        <div className="text-xs">
          <span className="text-text-tertiary">Range: </span>
          <span className="text-text-secondary">{def.range}</span>
        </div>
      </div>
    </div>
  );

  return (
    <Tooltip content={content} position={position} maxWidth={320} showIcon>
      {children}
    </Tooltip>
  );
}

function InfoIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
      />
    </svg>
  );
}

export default Tooltip;
